import { primaryButtonSurface } from '@/components/web-fe/design-v2/surfaces';
import { cn } from '@listening/utils/react-helpers';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { DocumentInsightsLoading } from './document-insights-loading';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useReadAlongSidebarStore } from './read-along-store';
import { Duration, Schema } from 'effect';
import { HttpApiError } from 'effect/unstable/httpapi';
import { suggestedDocumentInsightsModel } from '@listening/api-contract/document-insights';
import {
  loadSavedDocumentInsights,
  ensureSavedDocumentInsights,
  savedDocumentInsightsKey,
} from '@listening/frontend/query/document-insights';
import {
  DocumentInsightsFailedError,
  RateLimitedError,
  BadRequestError,
  NotFoundError,
  UnauthorizedError,
} from '@listening/api-contract/errors';
import { useEq } from '@listening/frontend/query/runtime-context';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { unwrapEffectQueryFailure } from '@listening/frontend/query/query-error';
import { Button } from '@listening/frontend/components/radix/button';
import { DocumentInsightsResult } from './document-insights-result';
import * as m from '@/paraglide/messages';

function documentInsightsErrorMessage(error: unknown) {
  if (Schema.is(DocumentInsightsFailedError)(error)) {
    if (error.reason === 'no-text') return m.read_along_ai_no_text_error();
    if (error.reason === 'interrupted')
      return m.read_along_ai_interrupted_error();
    return m.read_along_ai_generation_error();
  }
  if (Schema.is(RateLimitedError)(error)) return m.try_magic_error_rate_limit();
  if (Schema.is(BadRequestError)(error))
    return m.read_along_ai_not_ready_error();
  if (Schema.is(NotFoundError)(error))
    return m.read_along_ai_document_unavailable_error();
  if (
    Schema.is(UnauthorizedError)(error) ||
    Schema.is(HttpApiError.Unauthorized)(error)
  )
    return m.read_along_ai_sign_in_error();
  return m.read_along_ai_connection_error();
}

const DocumentInsightsContext = createContext<ReturnType<
  typeof useDocumentInsightsGeneration
> | null>(null);

export function DocumentInsightsProvider({
  audioId,
  userId,
  enabled,
  children,
}: {
  audioId?: string;
  userId?: string;
  enabled: boolean;
  children: ReactNode;
}) {
  const generation = useDocumentInsightsGeneration(audioId, userId, enabled);
  return (
    <DocumentInsightsContext value={enabled ? generation : null}>
      {children}
    </DocumentInsightsContext>
  );
}

export function useDocumentInsights() {
  return useContext(DocumentInsightsContext);
}

function useDocumentInsightsGeneration(
  audioId: string | undefined,
  userId: string | undefined,
  enabled: boolean,
) {
  const eq = useEq();
  const { capture } = useAnalytics();
  const [openedFor, setOpenedFor] = useState<string>();
  const queryClient = useQueryClient();
  const { sidebarState } = useReadAlongSidebarStore();
  const selection = {
    model: suggestedDocumentInsightsModel(),
    context: 'full' as const,
  };
  const queryKey = savedDocumentInsightsKey(
    userId ?? '',
    audioId ?? '',
    selection,
  );
  const scope = queryKey.join(':');
  const opened = openedFor === scope;
  const ready = !!audioId && !!userId;
  const query = useQuery({
    ...eq.queryOptions({
      queryKey,
      queryFn: () => loadSavedDocumentInsights(audioId ?? '', selection),
    }),
    enabled: (query) =>
      enabled &&
      ready &&
      ((opened && sidebarState === 'information') ||
        query.state.data?.status === 'pending'),
    retry: false,
    staleTime: Duration.toMillis(Duration.minutes(1)),
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' && !query.state.error
        ? Duration.toMillis(Duration.seconds(2))
        : false,
  });
  const mutation = useMutation({
    ...eq.mutationOptions({ mutationFn: ensureSavedDocumentInsights }),
    retry: false,
    onMutate: async (input) => {
      const queryKey = savedDocumentInsightsKey(
        userId ?? '',
        input.audioId,
        input,
      );
      await queryClient.cancelQueries({ queryKey });
      return { queryKey, scope: queryKey.join(':') };
    },
    onSuccess: (saved, _input, started) =>
      queryClient.setQueryData(started.queryKey, saved),
  });
  const mutationForThisDocument = mutation.context?.scope === scope;
  const mutationPending = mutation.isPending && mutationForThisDocument;
  const mutationMatchesQuery =
    mutationForThisDocument &&
    mutation.variables?.retryRequestId === query.data?.requestId;
  const failure = unwrapEffectQueryFailure(
    (mutationMatchesQuery ? mutation.error : null) ?? query.error,
  );
  function generate() {
    if (!audioId || !userId || mutationPending) return;
    capture('document_insights_ui_interaction', {
      interaction: 'retry_clicked',
      audio_id: audioId,
    });
    if (query.data?.status !== 'failed') {
      mutation.reset();
      void query.refetch();
      return;
    }
    mutation.mutate({
      audioId,
      ...selection,
      requestId: crypto.randomUUID(),
      retryRequestId: query.data.requestId,
    });
  }
  return {
    ready,
    opened,
    open: () => {
      setOpenedFor(scope);
      if (audioId)
        capture('document_insights_ui_interaction', {
          interaction: 'sidebar_opened',
          audio_id: audioId,
        });
    },
    saved: query.data,
    loading: query.isFetching || mutationPending,
    failure,
    generate,
  };
}

export function DocumentAiContent() {
  const generation = useDocumentInsights();
  if (!generation) return null;
  const { saved, loading, failure, generate } = generation;
  if (saved?.status !== 'completed' && failure) {
    return (
      <DocumentInsightsRetry
        message={documentInsightsErrorMessage(failure)}
        onRetry={generate}
      />
    );
  }
  if (saved?.status === 'pending') {
    return (
      <DocumentInsightsLoading
        label={
          saved.progress?.phase === 'writing'
            ? m.read_along_ai_writing()
            : m.read_along_ai_preparing()
        }
      />
    );
  }
  if (!saved || (saved.status === 'failed' && loading)) {
    return <DocumentInsightsLoading label={m.mobile_loading()} />;
  }
  if (saved?.status === 'failed') {
    return (
      <DocumentInsightsRetry
        message={documentInsightsErrorMessage(
          new DocumentInsightsFailedError(saved.error),
        )}
        onRetry={generate}
      />
    );
  }
  const insights = saved?.status === 'completed' ? saved.result : undefined;
  return (
    <div className='min-h-0 flex-1 overflow-y-auto'>
      <header className='flex h-14 items-center px-6 pr-14'>
        <h2 className='truncate text-[15px] leading-[1.4] font-semibold text-[var(--v2-text-muted)]'>
          {m.read_along_ai_title()}
        </h2>
      </header>
      <DocumentInsightsResult
        description={insights?.description}
        summary={insights?.summary}
        keyPoints={insights?.keyPoints}
      />
    </div>
  );
}

function DocumentInsightsRetry({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className='flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-y-auto p-6 text-center'>
      <p
        role='alert'
        className='max-w-64 text-sm leading-relaxed text-[var(--v2-text-muted)]'
      >
        {message}
      </p>
      <Button
        variant='ghost'
        size='sm'
        className={cn(
          'shrink-0 px-4 font-semibold hover:bg-[var(--v2-cta-bg)] hover:text-[var(--v2-cta-text)]',
          primaryButtonSurface,
        )}
        onClick={onRetry}
      >
        {m.common_try_again()}
      </Button>
    </div>
  );
}
